import * as i0 from '@angular/core';
import { Injectable, Inject, InjectionToken, Component, ChangeDetectionStrategy, Input, HostBinding, EventEmitter, ContentChild, Output, Directive, HostListener, NgModule } from '@angular/core';
import * as i2 from '@angular/common';
import { DOCUMENT, CommonModule } from '@angular/common';
import * as i2$1 from '@angular/material/icon';
import { MatIconModule } from '@angular/material/icon';
import * as i1$1 from '@angular/material/progress-spinner';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { of, Subject, BehaviorSubject, fromEvent } from 'rxjs';
import * as i1 from '@angular/router';
import { timeout, throttleTime, map, filter } from 'rxjs/operators';
import * as i1$2 from '@angular/cdk/scrolling';
import { BlockScrollStrategy } from '@angular/cdk/overlay';

class FsScrollService {
    constructor(_document, _route, _router, _location) {
        this._document = _document;
        this._route = _route;
        this._router = _router;
        this._location = _location;
        this._instances = window.FsScrollServiceInstances;
        this._pendingInstances = window.FsScrollServicePendingInstances;
        if (!window.FsScrollServiceInstances) {
            window.FsScrollServiceInstances = new Map();
            this._instances = window.FsScrollServiceInstances;
        }
        if (!window.FsScrollServicePendingInstances) {
            window.FsScrollServicePendingInstances = new Map();
            this._pendingInstances = window.FsScrollServicePendingInstances;
        }
    }
    pushInstance(instance) {
        if (!instance.name) {
            throw Error('FsScroll must have a name');
        }
        this._instances.set(instance.name, instance);
        if (this._pendingInstances.has(instance.name)) {
            const subject = this._pendingInstances.get(instance.name);
            subject.next(instance);
            subject.complete();
        }
    }
    component(name) {
        if (this._instances.has(name)) {
            return of(this._instances.get(name));
        }
        else {
            const subject = new Subject();
            this._pendingInstances.set(name, subject);
            return subject.pipe(timeout(1000));
        }
    }
    remove(name) {
        const instance = this._instances.get(name);
        if (instance) {
            instance.destroy();
        }
        this._instances.delete(name);
    }
    scrollToAnchor(name, args) {
        const elem = this._document.querySelector(`[fsScrollAnchor="${name}"]`);
        if (!!elem) {
            const scrollArgs = Object.assign({
                block: 'center',
                behavior: 'smooth',
            }, args);
            elem.scrollIntoView(scrollArgs);
        }
    }
    scrollToQueryParamAnchor(args) {
        const anchor = this._route.snapshot.queryParams.anchor;
        this.scrollToAnchor(anchor, args);
    }
    setAnchor(name) {
        const url = this._router
            .createUrlTree([], {
            relativeTo: this._route,
            queryParams: { anchor: name },
            queryParamsHandling: 'merge',
        })
            .toString();
        this._location.replaceState(url);
    }
    clearAnchor() {
        this.setAnchor(null);
    }
}
FsScrollService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsScrollService, deps: [{ token: DOCUMENT }, { token: i1.ActivatedRoute }, { token: i1.Router }, { token: i2.Location }], target: i0.ɵɵFactoryTarget.Injectable });
FsScrollService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsScrollService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsScrollService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i1.ActivatedRoute }, { type: i1.Router }, { type: i2.Location }]; } });

const FS_SCROLL_CONFIG = new InjectionToken('fs-scroll-config');

class FsScrollInstance {
    constructor(config, _fsScroll, _zone) {
        this.config = config;
        this._fsScroll = _fsScroll;
        this._zone = _zone;
        this.enabled = true;
        this._loading = new BehaviorSubject(false);
        this._completed = new BehaviorSubject(false);
        this._loaded = new Subject();
        this._load = new Subject();
        this.reloadSubscription = null;
        this.contentHeight = null;
        this.scrollTop = 0;
        this.config = Object.assign({}, config);
        this.config.spinnerDiameter = this.config.spinnerDiameter || 40;
        this.config.scrollThreshold = this.config.scrollThreshold || 1000;
    }
    init(component) {
        this._component = component;
        this._el = this._component._el;
        if (this.name) {
            this._fsScroll.pushInstance(this);
        }
        this._zone.runOutsideAngular(() => {
            this.subscribeToScroll();
            this.subscribeDown();
        });
    }
    get isLoading() {
        return this._loading.getValue();
    }
    get isLoading$() {
        return this._loading.asObservable();
    }
    get completed$() {
        return this._completed.asObservable();
    }
    get completed() {
        return this._completed.getValue();
    }
    complete() {
        this._completed.next(true);
    }
    get loadingObservable() {
        return this._loading.asObservable();
    }
    get loadedObservable() {
        return this._loaded.asObservable();
    }
    loading(value) {
        this._loading.next(value === undefined || value);
    }
    loaded() {
        this.loading(false);
        this._loaded.next(true);
    }
    destroy() {
        this._loading.complete();
        this._loaded.complete();
        this._load.complete();
        this._completed.complete();
    }
    subscribe(fn) {
        setTimeout(() => {
            if (this._component.scrollContentComponent) {
                this._contentElement = this._component.scrollContentComponent.el;
                this.reload();
            }
            else {
                console.warn('Missing the fsScrollContent component');
            }
        });
        return this._load.asObservable().subscribe(fn);
    }
    reload() {
        this.contentHeight = null;
        this.scrollTop = 0;
        if (this.reloadSubscription) {
            this.reloadSubscription.unsubscribe();
        }
        this.reloadSubscription = this.loadedObservable.subscribe(() => {
            this._reload();
        });
        this._reload();
    }
    _reload() {
        if (!this._contentElement) {
            return;
        }
        // Delay to allow for rendering
        setTimeout(() => {
            const contentHeight = this._contentElement.nativeElement.offsetHeight;
            if (!this.completed &&
                contentHeight <= this._el.nativeElement.offsetHeight &&
                this.contentHeight !== contentHeight) {
                this._load.next(this);
            }
            else if (this.reloadSubscription) {
                this.reloadSubscription.unsubscribe();
            }
            this.contentHeight = contentHeight;
        });
    }
    subscribeToScroll() {
        this._scroll = fromEvent(this._el.nativeElement, 'scroll')
            .pipe(throttleTime(20), map((e) => ({
            scrollHeight: e.target.scrollHeight,
            scrollTop: e.target.scrollTop,
            clientHeight: e.target.clientHeight
        })));
    }
    subscribeDown() {
        this._scroll
            .pipe(throttleTime(20), filter(() => !this.isLoading && !this.completed && this.enabled), filter(e => {
            return this.isUserScrollingDown(e) && this.isScrollThresholdExceeded(e);
        }))
            .subscribe(() => {
            this._load.next(this);
        });
    }
    isUserScrollingDown(e) {
        const down = e.scrollTop > this.scrollTop;
        this.scrollTop = e.scrollTop;
        return down;
    }
    isScrollThresholdExceeded(e) {
        return e.scrollHeight < (e.scrollTop + e.clientHeight + this.config.scrollThreshold);
    }
}
FsScrollInstance.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsScrollInstance, deps: [{ token: FS_SCROLL_CONFIG }, { token: FsScrollService }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Injectable });
FsScrollInstance.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsScrollInstance });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsScrollInstance, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [FS_SCROLL_CONFIG]
                }] }, { type: FsScrollService }, { type: i0.NgZone }]; } });

class FsScrollContentFooterComponent {
    constructor() {
        this.loading = false;
        this.completed = false;
    }
}
FsScrollContentFooterComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsScrollContentFooterComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
FsScrollContentFooterComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.16", type: FsScrollContentFooterComponent, selector: "fs-scroll-content-footer", inputs: { loading: "loading", completed: "completed", completeMessage: "completeMessage", completeIcon: "completeIcon", spinnerDiameter: "spinnerDiameter" }, ngImport: i0, template: "<ng-content></ng-content>\n<mat-spinner *ngIf=\"loading\" [diameter]=\"spinnerDiameter\" class=\"fs-scroll-spinner\"></mat-spinner>\n\n<div class=\"scroll-complete\"\n     *ngIf=\"!loading\n     && completed\n     && (completeMessage || completeIcon)\n\">\n  <span *ngIf=\"completeMessage\" class=\"scroll-complete-message\">{{ completeMessage }}</span>\n  <span *ngIf=\"completeIcon\" class=\"scroll-complete-icon\"><mat-icon>{{ completeIcon }}</mat-icon></span>\n</div>\n\n<ng-template></ng-template>\n", styles: [".fs-scroll-spinner{margin:0 auto}.scroll-complete{display:flex;align-items:center;justify-content:center;color:#7d7d7f}.scroll-complete .scroll-complete-message,.scroll-complete .scroll-complete-icon{margin:0 4px}\n"], components: [{ type: i1$1.MatSpinner, selector: "mat-spinner", inputs: ["color"] }, { type: i2$1.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }], directives: [{ type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsScrollContentFooterComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fs-scroll-content-footer',
                    templateUrl: 'scroll-content-footer.component.html',
                    styleUrls: ['scroll-content-footer.component.scss'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return []; }, propDecorators: { loading: [{
                type: Input
            }], completed: [{
                type: Input
            }], completeMessage: [{
                type: Input
            }], completeIcon: [{
                type: Input
            }], spinnerDiameter: [{
                type: Input
            }] } });

class FsScrollContentComponent {
    constructor(el, instance) {
        this.el = el;
        this.instance = instance;
        this.selfClass = true;
    }
}
FsScrollContentComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsScrollContentComponent, deps: [{ token: i0.ElementRef }, { token: FsScrollInstance }], target: i0.ɵɵFactoryTarget.Component });
FsScrollContentComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.16", type: FsScrollContentComponent, selector: "[fsScrollContent]", host: { properties: { "class.fs-scroll-content": "this.selfClass" } }, ngImport: i0, template: "<ng-content></ng-content>\n\n<fs-scroll-content-footer\n  [loading]=\"instance?.isLoading$ | async\"\n  [completed]=\"instance?.completed$ | async\"\n  [completeMessage]=\"instance?.config.completeMessage\"\n  [completeIcon]=\"instance?.config.completeIcon\"\n  [spinnerDiameter]=\"instance?.config.spinnerDiameter\"\n>\n</fs-scroll-content-footer>\n", styles: [":host ::ng-deep .fs-scroll-content{display:block}\n"], components: [{ type: FsScrollContentFooterComponent, selector: "fs-scroll-content-footer", inputs: ["loading", "completed", "completeMessage", "completeIcon", "spinnerDiameter"] }], pipes: { "async": i2.AsyncPipe }, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsScrollContentComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[fsScrollContent]',
                    templateUrl: 'scroll-content.component.html',
                    styleUrls: ['scroll-content.component.scss'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: FsScrollInstance }]; }, propDecorators: { selfClass: [{
                type: HostBinding,
                args: ['class.fs-scroll-content']
            }] } });

class FsScrollComponent {
    constructor(fsScrollService, instance, _el, config) {
        this.fsScrollService = fsScrollService;
        this.instance = instance;
        this._el = _el;
        this.config = config;
        this._load = new EventEmitter;
        this.selfClass = true;
    }
    set name(value) {
        this.instance.name = value;
    }
    set scrollThreshold(value) {
        this.instance.config.scrollThreshold = value;
    }
    set completed(value) {
        this.instance.complete();
    }
    set enabled(value) {
        this.instance.enabled = value;
    }
    set loading(value) {
        this.instance.loading(value);
    }
    set completeMessage(value) {
        this.instance.config.completeMessage = value;
    }
    set completeIcon(value) {
        this.instance.config.completeIcon = value;
    }
    get load() {
        return this._load;
    }
    get loadingChange() {
        return this.instance.loadingObservable;
    }
    ngOnInit() {
        this.instance.init(this);
        if (this._load.observers.length) {
            this.instance.subscribe(() => {
                this._load.emit(this.instance);
            });
        }
    }
    ngOnDestroy() {
        this.instance.destroy();
    }
}
FsScrollComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsScrollComponent, deps: [{ token: FsScrollService }, { token: FsScrollInstance }, { token: i0.ElementRef }, { token: FS_SCROLL_CONFIG }], target: i0.ɵɵFactoryTarget.Component });
FsScrollComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.16", type: FsScrollComponent, selector: "[fsScroll]", inputs: { name: "name", scrollThreshold: "scrollThreshold", completed: "completed", enabled: "enabled", loading: "loading", completeMessage: "completeMessage", completeIcon: "completeIcon" }, outputs: { load: "load", loadingChange: "loadingChange" }, host: { properties: { "class.fs-scroll": "this.selfClass" } }, providers: [FsScrollInstance], queries: [{ propertyName: "scrollContentComponent", first: true, predicate: FsScrollContentComponent, descendants: true }], ngImport: i0, template: "<ng-content></ng-content>\n", styles: ["::ng-deep .fs-scroll{overflow-y:auto;-webkit-overflow-scrolling:touch}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsScrollComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[fsScroll]',
                    templateUrl: 'scroll.component.html',
                    styleUrls: ['scroll.component.scss'],
                    providers: [FsScrollInstance],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: FsScrollService }, { type: FsScrollInstance }, { type: i0.ElementRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [FS_SCROLL_CONFIG]
                }] }]; }, propDecorators: { scrollContentComponent: [{
                type: ContentChild,
                args: [FsScrollContentComponent]
            }], name: [{
                type: Input,
                args: ['name']
            }], scrollThreshold: [{
                type: Input
            }], completed: [{
                type: Input
            }], enabled: [{
                type: Input
            }], loading: [{
                type: Input
            }], completeMessage: [{
                type: Input
            }], completeIcon: [{
                type: Input
            }], load: [{
                type: Output
            }], loadingChange: [{
                type: Output
            }], selfClass: [{
                type: HostBinding,
                args: ['class.fs-scroll']
            }] } });

class FsDocumentScrollService {
    constructor(viewportRuler) {
        this.viewportRuler = viewportRuler;
    }
    disable() {
        this.getBlockScrollStrategy().enable();
    }
    enable() {
        this.getBlockScrollStrategy().disable();
    }
    getBlockScrollStrategy() {
        if (!this._blockScrollStrategy) {
            this._blockScrollStrategy = new BlockScrollStrategy(this.viewportRuler, window.document);
        }
        return this._blockScrollStrategy;
    }
}
FsDocumentScrollService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsDocumentScrollService, deps: [{ token: i1$2.ViewportRuler }], target: i0.ɵɵFactoryTarget.Injectable });
FsDocumentScrollService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsDocumentScrollService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsDocumentScrollService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$2.ViewportRuler }]; } });

class FsScrollAnchorDirective {
    constructor(_scrollService) {
        this._scrollService = _scrollService;
    }
    onClick(button, ctrlKey, shiftKey, altKey, metaKey) {
        if (button !== 0 || ctrlKey || shiftKey || altKey || metaKey) {
            return true;
        }
        if (typeof this.target === 'string' && this.target != '_self') {
            return true;
        }
        this._scrollService.setAnchor(this.anchor);
    }
}
FsScrollAnchorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsScrollAnchorDirective, deps: [{ token: FsScrollService }], target: i0.ɵɵFactoryTarget.Directive });
FsScrollAnchorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.16", type: FsScrollAnchorDirective, selector: "[fsScrollAnchor]", inputs: { anchor: ["fsScrollAnchor", "anchor"], target: "target" }, host: { listeners: { "click": "onClick($event.button,$event.ctrlKey,$event.shiftKey,$event.altKey,$event.metaKey)" }, properties: { "attr.fsScrollAnchor": "this.anchor" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsScrollAnchorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fsScrollAnchor]'
                }]
        }], ctorParameters: function () { return [{ type: FsScrollService }]; }, propDecorators: { anchor: [{
                type: Input,
                args: ['fsScrollAnchor']
            }, {
                type: HostBinding,
                args: ['attr.fsScrollAnchor']
            }], target: [{
                type: Input
            }], onClick: [{
                type: HostListener,
                args: ['click',
                    ['$event.button', '$event.ctrlKey', '$event.shiftKey', '$event.altKey', '$event.metaKey']]
            }] } });

class FsScrollModule {
    static forRoot(config) {
        return {
            ngModule: FsScrollModule,
            providers: [
                FsScrollService,
                FsDocumentScrollService,
                {
                    provide: FS_SCROLL_CONFIG,
                    useValue: config || {}
                }
            ]
        };
    }
}
FsScrollModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsScrollModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FsScrollModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsScrollModule, declarations: [FsScrollComponent,
        FsScrollContentComponent,
        FsScrollContentFooterComponent,
        FsScrollAnchorDirective], imports: [CommonModule,
        MatProgressSpinnerModule,
        MatIconModule], exports: [FsScrollComponent,
        FsScrollContentComponent,
        FsScrollAnchorDirective] });
FsScrollModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsScrollModule, providers: [
        FsDocumentScrollService
    ], imports: [[
            CommonModule,
            MatProgressSpinnerModule,
            MatIconModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsScrollModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        MatProgressSpinnerModule,
                        MatIconModule,
                    ],
                    exports: [
                        FsScrollComponent,
                        FsScrollContentComponent,
                        FsScrollAnchorDirective,
                    ],
                    declarations: [
                        FsScrollComponent,
                        FsScrollContentComponent,
                        FsScrollContentFooterComponent,
                        FsScrollAnchorDirective,
                    ],
                    providers: [
                        FsDocumentScrollService
                    ]
                }]
        }] });

/*
 * Public API Surface
 */

/**
 * Generated bundle index. Do not edit.
 */

export { FS_SCROLL_CONFIG, FsDocumentScrollService, FsScrollAnchorDirective, FsScrollComponent, FsScrollContentComponent, FsScrollInstance, FsScrollModule, FsScrollService };
